<template>
  <sidebar-row class="py-2">
    <v-row>
      <v-col cols="7">
        <data-input
          disabled-sorting
          :menu-props="{ maxWidth: 250, offsetY: true }"
          v-model="currentProfile"
          :items="profiles"
          return-object
          item-text="name"
          item-value="id"
          :no-data-text="$i18n.tc('sidebar.project', 0)"
          :data-type="{ name: 'select' }"
          hide-details
        >
        </data-input>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto" style="display: flex" class="align-center">
        <base-rounded-button
          icon="mdi-content-save"
          size="x-small"
          :tooltip="$i18n.t('button.saveProfile')"
          :loading="isProfileSaveLoading"
          :disabled="!currentProfile || !isAddProfileDisabled"
          @click="saveCurrentProfile"
        />
        <base-rounded-button
          icon="mdi-plus"
          size="x-small"
          :tooltip="$i18n.t('button.addProfile')"
          :disabled="!isAddProfileDisabled"
          color="success"
          @click="openProfileDialog('new')"
        />
        <dots-menu
          :items="dotsMenuItems"
          v-on="$listeners"
          @delete="isDeleteDialogVisible = true"
          @changeName="openProfileDialog('edit')"
        ></dots-menu>
      </v-col>
    </v-row>
    <main-dialog
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t(translationPaths.deleteProfileDialogTitle)"
      :body="$i18n.t('dialog.body.deleteProfileConfirmation')"
      :loading="isDeleteDialogLoading"
      @agree="deleteCurrentProfile"
      agree-text="dialog.agreeDelete"
    />
    <main-input-dialog
      :is-visible.sync="isProfileDialogVisible"
      :loading="isProfileDialogLoading"
      :title-translation="
        profileDialogNew
          ? $i18n.t(translationPaths.addProfileDialogTitle)
          : $i18n.t(translationPaths.editProfileDialogTitle)
      "
      :agree-text="profileDialogNew ? 'dialog.agreeAdd' : 'dialog.agreeEdit'"
      dialog-width="30%"
      @agree="profileAgreeHandler"
      :inputs="profileDialogInputs"
    >
      <template #[`profileName.input`]="data">
        <v-col cols="6">
          <data-input v-model="newProfileName" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </sidebar-row>
</template>

<script>
import DotsMenu from '@/components/DotsMenu';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'ProfilesWidget',
  components: {
    DotsMenu,
    SidebarRow,
    DataInput: () => import('@/components/DataInput'),
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  data: () => ({
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isProfileSaveLoading: false,
    profileDialogNew: true,
    isProfileDialogVisible: false,
    isProfileDialogLoading: false,
    newProfileName: '',
    currentProfile: null,
  }),
  props: {
    isAddProfileDisabled: {
      type: Boolean,
      default: false,
    },
    currentData: {
      type: Object,
      default: () => ({}),
    },
    translationPaths: {
      type: Object,
      default: () => ({
        addProfileDialogTitle: 'dialog.title.profiles.defaultAdd',
        editProfileDialogTitle: 'dialog.title.profiles.defaultEdit',
        deleteProfileDialogTitle: 'dialog.title.profiles.defaultDelete',
      }),
    },
    profiles: {
      type: Array,
      default: null,
    },
    getProfiles: {
      type: Function,
      default: () => {},
    },
    saveProfile: {
      type: Function,
      default: () => {},
    },
    editProfile: {
      type: Function,
      default: () => {},
    },
    deleteProfile: {
      type: Function,
      default: () => {},
    },
  },
  computed: {
    dotsMenuItems() {
      return [
        {
          name: 'changeName',
          disabled: !this.currentProfile,
        },
        {
          name: 'delete',
          disabled: !this.currentProfile,
        },
      ];
    },
    profileDialogInputs() {
      return [
        {
          name: 'profileName',
          translationPath: 'dialog.name',
          dataType: {
            name: 'text',
          },
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
  },
  watch: {
    currentProfile(nV) {
      this.$emit('changeCurrentProfile', nV);
    },
  },
  methods: {
    setNewProfile(id) {
      this.currentProfile = this.profiles.find(profile => profile.id === id);
    },
    async profileAgreeHandler() {
      this.isProfileDialogLoading = true;
      try {
        if (this.profileDialogNew) {
          const payload = {
            ...this.currentData,
            name: this.newProfileName,
          };
          const profileId = await this.saveProfile(payload);
          this.setNewProfile(profileId);
          this.isProfileSaveLoading = false;
          this.isProfileDialogVisible = false;
          this.newProfileName = '';
        } else {
          let currentProfileCopy = JSON.parse(JSON.stringify(this.currentProfile));
          const id = currentProfileCopy.id;
          delete currentProfileCopy.id;
          delete currentProfileCopy.default;
          currentProfileCopy.name = this.newProfileName;
          await this.editProfile({
            profileId: id,
            payload: currentProfileCopy,
          });
          this.setNewProfile(id);
          this.isProfileDialogVisible = false;
          this.newProfileName = '';
        }
      } catch {
        this.isProfileSaveLoading = false;
      }
    },
    async saveCurrentProfile() {
      this.isProfileSaveLoading = true;
      try {
        let currentProfileCopy = JSON.parse(JSON.stringify(this.currentData));
        await this.editProfile({
          profileId: this.currentProfile.id,
          payload: currentProfileCopy,
        });
        this.setNewProfile(this.currentProfile.id);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successProfileSave'),
        });
        this.isProfileSaveLoading = false;
      } catch {
        this.isProfileSaveLoading = false;
      }
    },
    async deleteCurrentProfile() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteProfile(this.currentProfile.id);
        this.currentProfile = null;
        this.isDeleteDialogLoading = false;
        this.isDeleteDialogVisible = false;
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    openProfileDialog(type) {
      this.isProfileDialogVisible = true;
      if (type === 'new') {
        this.profileDialogNew = true;
        this.newProfileName = '';
      } else {
        this.profileDialogNew = false;
        this.newProfileName = this.currentProfile.name;
      }
    },
    async init() {
      await this.getProfiles();
      const defaultProfile = this.profiles.find(profile => profile.default);
      this.currentProfile = defaultProfile || null;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
